import UserEmployeeDetailAPIView from "issara-sdk/apis/UserEmployeeDetailAPIView_users";

import ApproveOfficialAccountRequest from "issara-sdk/apis/ApproveOfficialAccountRequest_apps_PRXM";
import DivisionHasUserBulkUpdate from "issara-sdk/apis/DivisionHasUserBulkUpdate_apps_PRX";
import OfficialAccountInvitationLink from "issara-sdk/apis/OfficialAccountInvitationLink_apps_PRX";
import OfficialAccountRequestDetail from "issara-sdk/apis/OfficialAccountRequestDetail_apps_PRXM";
import OfficialAccountRequestList from "issara-sdk/apis/OfficialAccountRequestList_apps_PRX";
import OfficialAccountUserEdit from "issara-sdk/apis/OfficialAccountUserEdit_apps_PRX";
import OfficialAccountUserList from "issara-sdk/apis/OfficialAccountUserList_apps_PRX";

import OAInvitationSerializer from "issara-sdk/types/OAInvitationSerializer_apps_PRX";
import ProfiledDivisionHasUserSerializer from "issara-sdk/types/ProfiledDivisionHasUserSerializer_apps_PRX";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { ADMIN_MENU_LIST } from "react-lib/apps/HISV3/HRM/HRMInterface";

import { AdminMenuKeys, formatMenuItems } from "react-lib/apps/HISV3/HRM/sequence/DSPermission";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  APPROVE_INVITE: "APPROVE_INVITE",
  DELETE_INVITE: "DELETE_INVITE",
  DELETE_MEMBER: "DELETE_MEMBER",
  GET_PROFILE: "GET_PROFILE",
  INVITE_MEMBER: "INVITE_MEMBER",
  SAVE_MEMBER: "SAVE_MEMBER",
  SEARCH_MEMBER: "SEARCH_MEMBER",
  SEARCH_REQUEST: "SEARCH_REQUEST",
} as const;

export const SEQUENCE_NAME = "PermissionManagement" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  PermissionManagementSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    inviteLink: string;
    oaRequestList: OAInvitationType[];
    oaUserList: ProfiledDivisionHasUserType[];
    userProfile: Record<string, any> | null;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "selectedDivision">;

export type OAInvitationType = OAInvitationSerializer;

export type ProfiledDivisionHasUserType = {
  id?: number;
  adminMenuList: string[];
  extra?: string;
  fullname: string;
  is_Active: boolean;
  isEditing?: boolean;
  mobileMenuList: string[];
  position?: number;
  user: number;
  user_employee: ProfiledDivisionHasUserSerializer["user_employee"];
  user_profile?: ProfiledDivisionHasUserSerializer["user_profile"];
};

export type MasterOptions = MasterMapOptions<typeof Masters>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.APPROVE_INVITE]: { id: number; onSuccess: () => void };
  [ACTIONS.DELETE_INVITE]: { id: number; onSuccess: () => void };
  [ACTIONS.DELETE_MEMBER]: { id: number; onSuccess: () => void };
  [ACTIONS.GET_PROFILE]: {
    id: number;
    image?: string | null;
    user_profile?: ProfiledDivisionHasUserSerializer["user_profile"];
  };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  PermissionManagementSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["positionAll", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      PermissionManagementSequence: {
        ...state.PermissionManagementSequence,
        sequenceIndex: "Action",
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH_REQUEST });
      Action(controller, { ...params, action: ACTIONS.SEARCH_MEMBER });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.APPROVE_INVITE]: HandleApproveInvite,
    [ACTIONS.DELETE_INVITE]: HandleDeleteInvite,
    [ACTIONS.DELETE_MEMBER]: HandleDeleteMember,
    [ACTIONS.GET_PROFILE]: HandleGetProfile,
    [ACTIONS.INVITE_MEMBER]: HandleInviteMember,
    [ACTIONS.SAVE_MEMBER]: HandleSaveMember,
    [ACTIONS.SEARCH_MEMBER]: HandleSearchMember,
    [ACTIONS.SEARCH_REQUEST]: HandleSearchRequest,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */

const HandleSearchRequest: Handler<unknown, Promise<void>> = async (controller) => {
  const [result] = await OfficialAccountRequestList.list({
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  const state = controller.getState();

  controller.setState({
    PermissionManagementSequence: {
      ...state.PermissionManagementSequence,
      oaRequestList: result?.items ?? [],
    },
  });
};

const HandleInviteMember: Handler<Params<Actions["INVITE_MEMBER"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.INVITE_MEMBER]: BLC_STATUS.LOADING },
  });

  const [result, error] = await OfficialAccountInvitationLink.get({
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  const origin = globalThis.location.origin;
  const inviteLink = `${origin}/?requestOA=${result.token}`;

  controller.setState({
    PermissionManagementSequence: { ...state.PermissionManagementSequence, inviteLink },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.INVITE_MEMBER]: null },
  });
};

const HandleDeleteInvite: Handler<Params<Actions["DELETE_INVITE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE_INVITE]: BLC_STATUS.LOADING },
  });

  const [, error] = await OfficialAccountRequestDetail.delete({
    apiToken: controller.apiToken,
    params,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnError: null, error });

    return;
  }

  await HandleSearchRequest(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE_INVITE]: null },
  });

  params.onSuccess();
};

const HandleApproveInvite: Handler<Params<Actions["APPROVE_INVITE"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.APPROVE_INVITE}_${params.id}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const [result, error] = await ApproveOfficialAccountRequest.post({
    apiToken: controller.apiToken,
    pk: params.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, action: btnKey, btnError: null, error });

    return;
  }

  await DivisionHasUserBulkUpdate.patch({
    apiToken: controller.apiToken,
    data: { extra: JSON.stringify({ is_Active: true }) },
    params: { id: result.id },
  });

  await Promise.all([HandleSearchRequest(controller), HandleSearchMember(controller)]);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });

  params.onSuccess();
};

const HandleSearchMember: Handler<unknown, Promise<void>> = async (controller) => {
  const [result] = await OfficialAccountUserList.list({
    apiToken: controller.apiToken,
    params: {},
    extra: { division: controller.data.division },
  });

  const state = controller.getState();

  const items: ProfiledDivisionHasUserSerializer[] = result?.items ?? [];

  const formattedItems: ProfiledDivisionHasUserType[] = items.map((item) => {
    const extra = JSON.parse(item.extra || "{}");
    const adminMenuList: AdminMenuKeys[] = extra.adminMenuList ?? [];
    const mobileMenuList: string[] = extra.mobileMenuList ?? [];

    return {
      id: item.id,
      adminMenuList: adminMenuList.filter((menu) => !Array.isArray(ADMIN_MENU_LIST[menu])),
      fullname: item.user_profile?.fullname || "",
      is_Active: extra.is_Active ?? false,
      mobileMenuList,
      position: item.user_position?.id,
      user: item.user,
      user_employee: item.user_employee,
      user_profile: item.user_profile,
      extra: item.extra,
    };
  });

  controller.setState({
    PermissionManagementSequence: {
      ...state.PermissionManagementSequence,
      oaUserList: formattedItems,
    },
  });
};

const HandleDeleteMember: Handler<Params<Actions["DELETE_MEMBER"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE_MEMBER]: BLC_STATUS.LOADING },
  });

  const [, error] = await OfficialAccountUserEdit.delete({
    apiToken: controller.apiToken,
    pk: params.id,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnError: null, error });

    return;
  }

  await HandleSearchMember(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE_MEMBER]: null },
  });

  params.onSuccess();
};

const HandleSaveMember: Handler<Params<Actions["SAVE_MEMBER"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_MEMBER]: BLC_STATUS.LOADING },
  });

  const oaUserList = state.PermissionManagementSequence?.oaUserList ?? [];
  const filteredItems = oaUserList.filter((item) => item.isEditing);
  const filteredPosition = filteredItems.filter((item) => item.position);
  const formattedDhu = filteredItems.map((item) => {
    const uniqueMenuItems = formatMenuItems(item.adminMenuList);

    const extra = {
      ...JSON.parse(item.extra || "{}"),
      adminMenuList: uniqueMenuItems,
      is_Active: item.is_Active,
      mobileMenuList: item.mobileMenuList,
    };

    return { id: item.id, extra: JSON.stringify(extra) };
  });

  const empPromises = filteredPosition.map(
    async (item) =>
      UserEmployeeDetailAPIView.patch({
        apiToken: controller.apiToken,
        data: {
          ...item.user_employee,
          gender: undefined,
          position: item.position,
          use_id_card_address_as_contact: undefined,
        } as any,
        pk: item.user,
      }) as Promise<[unknown, unknown]>
  );

  const dhuPromises = formattedDhu.map(
    async (item) =>
      DivisionHasUserBulkUpdate.patch({
        apiToken: controller.apiToken,
        data: { extra: item.extra },
        params: { id: item.id },
      }) as Promise<[unknown, unknown]>
  );

  const responses = await Promise.all([...empPromises, ...dhuPromises]);

  const isError = responses.some((res) => res[1]);

  if (isError) {
    SetErrorMessage(controller, { ...params, error: responses.map((item) => item[1]) });

    return;
  }

  await HandleSearchMember(controller);

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_MEMBER]: BLC_STATUS.SUCCESS },
  });

  // params.onSuccess();
};

const HandleGetProfile: Handler<Params<Actions["GET_PROFILE"]>> = async (controller, params) => {
  const state = controller.getState();

  const btnKey = `${BTN_ACTS.GET_PROFILE}_${params.id}`;
  const userProfile = params.user_profile ?? {};

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
  });

  const [result] = await UserEmployeeDetailAPIView.retrieve({
    apiToken: controller.apiToken,
    pk: params.id,
  });

  const profile = {
    ...(result
      ? { ...result, cid: result.citizen_no, dob: result.birth_date }
      : {
          ...userProfile,
        }),
    image: params.image,
    user: params.id,
  };

  controller.setState({
    PermissionManagementSequence: {
      ...state.PermissionManagementSequence,
      userProfile: profile,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });
};
